/**
 * The gate every signed-in workspace passes through before the application renders.
 *
 * Two rules, both decided by the server and only carried out here:
 *
 *   - setup unfinished  -> the wizard replaces the application entirely, with no navigation.
 *   - read-only expired -> the reactivation wall replaces it, with only billing reachable.
 *
 * The wizard and the wall stand in place of the shell rather than inside it on purpose. A sidebar
 * full of destinations that would every one of them refuse is a worse answer than one screen that
 * says what is wrong and what to do about it.
 *
 * Both conditions come from GET /api/v1/tenant/access, which is computed by the same module that
 * decides what the next write is answered with. Nothing is inferred in the browser, so the screen
 * somebody is shown and the reply their next action receives cannot disagree.
 */

import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, jsonBody } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { isOwner as membershipIsOwner } from "../auth/permissions";
import { useSensitiveAction } from "../auth/SensitiveActionGate";
import { BrandMark } from "../brand/BrandMark";
import { OnboardingWizard } from "../features/onboarding/OnboardingWizard";
import { formatDateTime } from "../lib/format";
import { Card, CardHeader, ErrorPanel, Notice } from "../ui/components";
import { AppShellSkeleton } from "../ui/skeletons";
import {
  useWorkspaceAccessQuery,
  type WorkspaceAccess,
  WorkspaceAccessProvider,
} from "./workspaceAccess";

interface Subscription {
  status: string;
  planCode: string;
  billingInterval: "monthly" | "annual";
  billingEmail: string;
}

interface BillingStatus {
  subscription: Subscription;
}

interface Redirect {
  url: string;
}

/**
 * The reactivation wall.
 *
 * Signing in still works, and so does signing out: this is a wall somebody can pay their way
 * through, not a lockout. The data is still there and is not deleted by reaching this screen.
 */
function SubscriptionWall({ access }: { access: WorkspaceAccess }) {
  const auth = useAuth();
  const owner = membershipIsOwner(auth.session?.selectedMembership);
  const sensitive = useSensitiveAction({
    lockedTitle: "Confirm it is you",
    lockedDescription: "Billing changes are protected. Enter a current authenticator code first.",
  });
  const billing = useQuery({
    queryKey: ["billing", "status"],
    queryFn: () => api<BillingStatus>("/api/v1/billing/status"),
    enabled: owner,
  });
  const [working, setWorking] = useState("");
  const [error, setError] = useState<unknown>();

  async function open(label: string, path: string, body?: unknown) {
    setWorking(label);
    setError(undefined);
    try {
      const redirect = await api<Redirect>(path, {
        method: "POST",
        ...(body === undefined ? {} : { body: jsonBody(body) }),
      });
      window.location.assign(redirect.url);
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
      setWorking("");
    }
  }

  const subscription = billing.data?.subscription;
  return (
    <main className="gate-layout">
      <div className="gate-shell stack">
        <header className="gate-header">
          <BrandMark />
          <div>
            <h1>This workspace is closed until the subscription is reactivated</h1>
            <p>
              Everything you have is still here and nothing has been deleted. Reactivating opens it
              again exactly as you left it.
            </p>
          </div>
        </header>

        {error ? <ErrorPanel error={error} /> : null}
        {owner && billing.error ? <ErrorPanel error={billing.error} /> : null}

        <Notice tone="warning" title="What happened">
          {access.lapsedSince
            ? `The subscription ended on ${formatDateTime(access.lapsedSince)}. The workspace stayed readable for 30 days after that so there was time to reactivate or export, and that period has now passed.`
            : "The subscription is no longer active, and the read-only period after it has passed."}
        </Notice>

        {owner ? (
          <Card>
            <CardHeader
              title="Reactivate"
              description={
                subscription
                  ? `Billing contact: ${subscription.billingEmail}`
                  : "Start a subscription to reopen the workspace."
              }
            />
            {sensitive.view}
            <div className="inline">
              <button
                className="button primary"
                type="button"
                disabled={working !== "" || !sensitive.unlocked || !subscription}
                onClick={() =>
                  open("checkout", "/api/v1/billing/subscription-checkout", {
                    planCode: subscription?.planCode,
                    billingInterval: subscription?.billingInterval,
                  })
                }
              >
                {working === "checkout" ? "Opening Stripe..." : "Start a new subscription"}
              </button>
              <button
                className="button secondary"
                type="button"
                disabled={working !== "" || !sensitive.unlocked}
                onClick={() => open("portal", "/api/v1/billing/portal")}
              >
                {working === "portal" ? "Opening Stripe..." : "Manage billing in Stripe"}
              </button>
            </div>
          </Card>
        ) : (
          <Notice tone="info" title="Ask the workspace owner">
            Only the person who owns this workspace can reactivate the subscription. Everything
            opens again for everybody as soon as they do.
          </Notice>
        )}

        <div className="inline">
          <button
            className="button ghost"
            type="button"
            onClick={() => {
              void auth.signOut().then(() => window.location.replace("/login"));
            }}
          >
            Sign out
          </button>
        </div>
      </div>
    </main>
  );
}

export function WorkspaceGate({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const location = useLocation();
  // The platform console is our work rather than any one workspace's, and an operator may hold a
  // membership somewhere as well. Neither gate applies to it, and it must neither wait on a tenant
  // read nor provoke one, which is why this is decided before the fetch rather than after it.
  const platformSurface = location.pathname.startsWith("/operator");
  const access = useWorkspaceAccessQuery(!platformSurface);

  if (platformSurface || !auth.session?.selectedMembership) {
    return <WorkspaceAccessProvider value={null}>{children}</WorkspaceAccessProvider>;
  }
  if (access.isLoading) return <AppShellSkeleton />;

  // A failed read is not a reason to lock somebody out of a workspace that may be perfectly fine.
  // The server refuses what has to be refused regardless, so falling through is safe and a false
  // wall on a dropped request would not be.
  const data = access.data;
  if (!data) return <WorkspaceAccessProvider value={null}>{children}</WorkspaceAccessProvider>;

  let content: ReactNode = children;
  if (data.wall) content = <SubscriptionWall access={data} />;
  else if (data.onboarding.state !== "ready") content = <OnboardingWizard />;
  return <WorkspaceAccessProvider value={data}>{content}</WorkspaceAccessProvider>;
}
